import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { useApiQuery } from '../../../../../../api/hooks';
import apiClient from '../../../../../../api/apiClient';
import { usePreloaderStore } from '../../../../../../stores';
import Modal from '../../../controls/Modal';
import LabeledInput from '../../../controls/LabeledInput';
import SpiteToggle from '../SpiteToggle';
import ImageUpload from '../ImageUpload';
import FlaggedStepCard from './FlaggedStepCard';
import ChainCard from './ChainCard';
import { CHALLENGE_CHAINS_KEY } from './challengeKeys';
import { ensureChallengeHomeCategoryId, ensureChallengeHomeSubcategoryId } from './challengeUtils';

// Модалка челленджа — механика эстафеты, поэтому НЕ переиспользует стандартный TaskModal.
// Оставлены только релевантные поля (награда, лимит времени, картинка, удаление) + модерация звеньев.
export default function ChallengeModal({ task, neighborId, smenaId, flaggedSteps, onSaved, onChanged, onClose }) {
    const editing = !!task;
    const { enqueueSnackbar } = useSnackbar();
    const { show, hide } = usePreloaderStore();
    const [file, setFile] = useState(null);
    const [clearImage, setClearImage] = useState(false);
    const [form, setForm] = useState({
        title: task?.title || '',
        description: task?.description || '',
        conditions: task?.conditions || '',
        cost: task?.cost ?? 50,
        duration: task?.duration || '',
        isActive: editing ? task.isActive !== false : true
    });
    const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

    const { data: chains = [], refetch: refetchChains } = useApiQuery(
        CHALLENGE_CHAINS_KEY(task?.id),
        `/api/neighbor/admin/challenges/${task?.id}/chains`,
        { enabled: editing }
    );

    // Обрыв цепочки меняет и flagged-звенья, и список цепочек — обновляем оба источника.
    const moderationChanged = () => { onChanged(); refetchChains(); };

    const save = async () => {
        if (!form.title.trim()) {
            enqueueSnackbar('Укажите название', { variant: 'warning' });
            return;
        }
        show();
        try {
            // Категория и подкатегория — системные (__CHALLENGES__), пользователь их не выбирает,
            // но create требует оба id non-null — резолвим скрытые (создаём при отсутствии).
            const categoryId = task?.categoryId || await ensureChallengeHomeCategoryId(neighborId);
            const subcategoryId = task?.subcategoryId || await ensureChallengeHomeSubcategoryId(categoryId);
            const requestData = {
                ...(editing ? {} : { neighborId, smenaId }),
                categoryId,
                subcategoryId,
                title: form.title.trim(),
                description: form.description,
                conditions: form.conditions,
                cost: form.cost,
                isActive: form.isActive,
                // Фиксированные для челленджа: эстафета со временем звена, без механики покупки/дуэлей/рулетки.
                isChallenge: true,
                useTime: true,
                duration: form.duration || null,
                withTargetUser: false,
                requiresBothConfirmation: false,
                involvedInDaily: false,
                taskAvailableCountType: 'UNLIMITED',
                availableCount: null
            };
            const fd = new FormData();
            fd.append('request', new Blob([JSON.stringify(requestData)], { type: 'application/json' }));
            if (file) fd.append('file', file);
            if (editing) {
                await apiClient.patch(`/api/neighbor/admin/tasks/update?id=${task.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            } else {
                await apiClient.post('/api/neighbor/admin/tasks/create', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            }
            enqueueSnackbar(editing ? 'Челлендж обновлён' : 'Челлендж создан', { variant: 'success' });
            onSaved();
            onClose();
        } catch (e) { enqueueSnackbar(e.response?.data?.message || 'Ошибка при сохранении', { variant: 'error' }); }
        finally { hide(); }
    };

    const del = async () => {
        show();
        try {
            await apiClient.delete(`/api/neighbor/admin/tasks/delete?id=${task.id}`);
            enqueueSnackbar('Челлендж удалён', { variant: 'success' });
            onSaved();
            onClose();
        } catch (e) { enqueueSnackbar('Ошибка при удалении', { variant: 'error' }); }
        finally { hide(); }
    };

    // Цепочки: активная первой, дальше свежие сверху; нумерация сквозная от старейшей (как во фронте игрока).
    const sortedChains = [...chains].sort((a, b) => {
        if (a.status === 'ACTIVE' && b.status !== 'ACTIVE') return -1;
        if (b.status === 'ACTIVE' && a.status !== 'ACTIVE') return 1;
        return (b.id || 0) - (a.id || 0);
    });

    const footer = (
        <>
            {editing && <button type="button" className="btn btn-danger" style={{ marginRight: 'auto' }} onClick={del}>Удалить</button>}
            <button type="button" className="btn btn-ghost" onClick={onClose}>Отмена</button>
            <button type="button" className="btn btn-primary" onClick={save}>{editing ? 'Сохранить' : 'Создать'}</button>
        </>
    );

    return (
        <Modal open kicker="Челлендж" title={editing ? 'Редактирование челленджа' : 'Создать челлендж'} size="lg" onClose={onClose} footer={footer}>
            <LabeledInput label="Название" value={form.title} onChange={(e) => set('title', e.target.value)} />
            <div>
                <label className="ctl-label">Описание</label>
                <textarea className="textarea" value={form.description} onChange={(e) => set('description', e.target.value)} />
            </div>
            <div>
                <label className="ctl-label">Условия выполнения</label>
                <textarea className="textarea" value={form.conditions} onChange={(e) => set('conditions', e.target.value)} />
            </div>
            <div className="fr-grid2">
                <LabeledInput label="Награда (SOL)" type="number" value={String(form.cost)} onChange={(e) => set('cost', parseInt(e.target.value, 10) || 0)} />
                <LabeledInput label="Лимит времени звена (ЧЧ:ММ:СС)" value={form.duration} onChange={(e) => set('duration', e.target.value)} placeholder="02:00:00" />
            </div>
            <div className="sp-sec">
                <div className="sp-tgs">
                    <SpiteToggle label="Активен" checked={form.isActive} onChange={() => set('isActive', !form.isActive)} full />
                </div>
            </div>
            <div className="sp-sec">
                <h4>Изображение</h4>
                <ImageUpload
                    existingUrl={clearImage ? null : (task?.mediaFile?.mediaFileUrl || null)}
                    pendingFile={file}
                    onFileSelect={(f) => { setFile(f); setClearImage(false); }}
                    onClear={() => { setFile(null); setClearImage(true); }}
                />
            </div>

            {editing && (
                <div className="sp-sec">
                    <h4>На проверке {flaggedSteps.length > 0 && `(${flaggedSteps.length})`}</h4>
                    {flaggedSteps.length > 0 ? (
                        <div className="ch-list">
                            {flaggedSteps.map(item => <FlaggedStepCard key={item.step?.id} item={item} onChanged={moderationChanged} />)}
                        </div>
                    ) : <div className="bt-empty">Нет звеньев на проверке</div>}
                </div>
            )}

            {editing && (
                <div className="sp-sec">
                    <h4>Цепочки {chains.length > 0 && `(${chains.length})`}</h4>
                    {sortedChains.length > 0 ? (
                        <div className="chn-list">
                            {sortedChains.map((chain, idx) => (
                                <ChainCard key={chain.id} chain={chain} number={sortedChains.length - idx} onChanged={moderationChanged} />
                            ))}
                        </div>
                    ) : <div className="bt-empty">Цепочек ещё не было</div>}
                </div>
            )}
        </Modal>
    );
}

ChallengeModal.propTypes = {
    task: PropTypes.object,
    neighborId: PropTypes.number,
    smenaId: PropTypes.number,
    flaggedSteps: PropTypes.array,
    onSaved: PropTypes.func.isRequired,
    onChanged: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired
};

ChallengeModal.defaultProps = { task: null, neighborId: null, smenaId: null, flaggedSteps: [] };
